import { currencyFormatter } from "@common/helpers/currency";
import {
    ScheduleLifecycleProposal,
    ScheduleStopSettlement,
} from "@common/schemas/domains/accounting/scheduleLifecycle.schema";
import { ScheduleStopSettlementKind } from "@common/types/domains/accounting/schedule";

/** "Decide later" is the absence of a settlement, so it is not a backend kind. */
export enum ScheduleStopChoice {
    accelerate = "accelerate",
    reclass = "reclass",
    decideLater = "decideLater",
}

export interface ScheduleStopOptionCopy {
    choice: ScheduleStopChoice;
    title: string;
    description: string;
}

export const SCHEDULE_STOP_ACCOUNT_LABEL = "GL Account";
export const SCHEDULE_STOP_ACCOUNT_PLACEHOLDER = "Select an account";
export const SCHEDULE_STOP_SETTLED_TOAST =
    "Schedule stopped and remaining balance posted";
export const SCHEDULE_STOP_RECLASS_ONE_ENTITY_HINT =
    "Select schedules from one entity to reclassify.";

export type ScheduleStopSummary = Pick<
    ScheduleLifecycleProposal["summary"],
    | "remainingAfterStopAmount"
    | "recognitionAccountName"
    | "holdingAccountName"
    | "scheduleType"
    | "revenueSubMode"
    | "expenseSubMode"
>;

export function buildScheduleStopOptionsCopy(
    summary: ScheduleStopSummary,
): ScheduleStopOptionCopy[] {
    const remainingBalance = currencyFormatter.format(
        summary.remainingAfterStopAmount ?? 0,
    );
    const recognitionAccount =
        summary.recognitionAccountName ?? "the recognition account";
    const holdingAccount = summary.holdingAccountName ?? "the holding account";
    return [
        {
            choice: ScheduleStopChoice.accelerate,
            title: "Accelerate recognition",
            description: `Recognize the remaining ${remainingBalance} in ${recognitionAccount} now.`,
        },
        {
            choice: ScheduleStopChoice.reclass,
            title: "Reclassify to another account",
            description: `Move the remaining ${remainingBalance} from ${holdingAccount} to an account you choose.`,
        },
        {
            choice: ScheduleStopChoice.decideLater,
            title: "Decide later",
            description: `Keep the remaining balance in ${holdingAccount}. No entry is posted.`,
        },
    ];
}

export function getScheduleStopConfirmLabel(
    choice: ScheduleStopChoice | null,
): string {
    return choice === ScheduleStopChoice.accelerate ||
        choice === ScheduleStopChoice.reclass
        ? "Stop and post entry"
        : "Stop schedule";
}

/**
 * The request body for a choice, or null while the choice is incomplete
 * (nothing picked yet, or reclassify without an account).
 */
export function resolveScheduleStopSettlement(options: {
    choice: ScheduleStopChoice | null;
    reclassAccountId: string | null;
}): { settlement: ScheduleStopSettlement | undefined } | null {
    switch (options.choice) {
        case null:
            return null;
        case ScheduleStopChoice.decideLater:
            return { settlement: undefined };
        case ScheduleStopChoice.accelerate:
            return {
                settlement: { kind: ScheduleStopSettlementKind.accelerate },
            };
        case ScheduleStopChoice.reclass:
            return options.reclassAccountId === null
                ? null
                : {
                      settlement: {
                          kind: ScheduleStopSettlementKind.reclass,
                          releaseAccountId: options.reclassAccountId,
                      },
                  };
    }
}
